<script setup lang="ts">
import { useRouter } from 'vue-router';
import {
  FhDataTable,
  FhDataTableColumn,
  FhDropdownItem,
  FhDropdownMenu,
  FhDropdownSection,
  FhIconButton,
  FhSpinner,
  FhText,
  FhTooltip,
  FhIcon,
} from '@fareharbor-com/beacon-vue';
import { option, information } from '@fareharbor-com/beacon-vue/icons';
import ItemListAvailability from '@/components/itemList/ItemListAvailability.vue';
import ItemListBookmark from '@/components/itemList/ItemListBookmark.vue';
import ItemListCompany from '@/components/itemList/ItemListCompany.vue';
import ItemListDetails from '@/components/itemList/ItemListDetails.vue';
import ItemListImage from '@/components/itemList/ItemListImage.vue';
import ItemListLocation from '@/components/itemList/ItemListLocation.vue';
import ItemListQualityScore from '@/components/itemList/ItemListQualityScore.vue';
import ItemListRating from '@/components/itemList/ItemListRating.vue';
import ItemListTags from '@/components/itemList/ItemListTags.vue';
import BaseFlexbox from '@/components/ui/BaseFlexbox/BaseFlexbox.vue';
import type { Item } from '@/types';

interface ItemListTableProps {
  data: Item[];
  isLoading: boolean;
}
const router = useRouter();

const redirectToItemDetails = (itemId: string) => {
  router.push(`/items/${itemId}`);
};

const props = defineProps<ItemListTableProps>();
</script>

<template>
  <div class="table-container flex-1 overflow-y-scroll overflow-x-hidden mt-4">
    <BaseFlexbox
      v-if="props.isLoading"
      justifyContent="center"
      alignItems="center"
      class="h-full"
    >
      <FhSpinner />
    </BaseFlexbox>
    <FhDataTable
      table-layout="auto"
      v-else
      :data="props.data"
      data-test-Id="item-list-table"
    >
      <FhDataTableColumn name="id" align="start">
        <template #header>#</template>
        <template #custom="{ rowData }">
          <ItemListImage :row="rowData" />
        </template>
      </FhDataTableColumn>

      <FhDataTableColumn name="name">
        <template #header>Item</template>
        <template #custom="{ rowData }">
          <div data-test-id="item-row">
            <ItemListDetails :row="rowData" />
          </div>
        </template>
      </FhDataTableColumn>
      <FhDataTableColumn name="company">
        <template #header>Company</template>
        <template #custom="{ rowData }">
          <div data-test-id="company-row">
            <ItemListCompany :row="rowData" />
          </div>
        </template>
      </FhDataTableColumn>
      <FhDataTableColumn name="location">
        <template #header>Location</template>
        <template #custom="{ rowData }">
          <div data-test-id="location-row">
            <ItemListLocation :row="rowData" />
          </div>
        </template>
      </FhDataTableColumn>
      <FhDataTableColumn name="itemType">
        <template #header>Item Type</template>
        <template #custom="{ rowData }">
          <div data-test-id="item-type-row">
            <ItemListTags :row="rowData" />
          </div>
        </template>
      </FhDataTableColumn>
      <FhDataTableColumn name="availabilityCount" class="w-fit-content">
        <template #header>
          <div class="flex items-center gap-2">
            Availability
            <FhTooltip placement="top">
              <FhIcon :icon="information" size="sm" class="cursor-help" />
              <template #content>
                <div class="max-w-xs">
                  <FhText variant="body-sm">
                    Availabilities on the next 6 months.
                  </FhText>
                  <FhText variant="body-sm" class="mt-2">
                    <strong>Classification:</strong>
                    <br />
                    Very Low: Less than 30
                    <br />
                    Low: Between 30-59
                    <br />
                    Standard: Between 60-120
                    <br />
                    High: More than 120
                  </FhText>
                </div>
              </template>
            </FhTooltip>
          </div>
        </template>
        <template #custom="{ rowData }">
          <ItemListAvailability :row="rowData" />
        </template>
      </FhDataTableColumn>
      <FhDataTableColumn name="rating">
        <template #header>Rating</template>
        <template #custom="{ rowData }">
          <div data-test-id="rating-row">
            <ItemListRating :row="rowData" />
          </div>
        </template>
      </FhDataTableColumn>
      <FhDataTableColumn name="qualityScore">
        <template #header>
          <div class="flex items-center gap-2">
            <span class="whitespace-nowrap">Quality Score</span>
            <FhTooltip placement="top">
              <FhIcon :icon="information" size="sm" class="cursor-help" />
              <template #content>
                <div class="max-w-xs">
                  <FhText variant="body-sm">
                    Reflects listing quality based on images, content,
                    availability and reviews - higher score indicates a higher
                    quality product.
                  </FhText>
                </div>
              </template>
            </FhTooltip>
          </div>
        </template>
        <template #custom="{ rowData }">
          <div data-test-id="quality-score-row">
            <ItemListQualityScore :row="rowData" />
          </div>
        </template>
      </FhDataTableColumn>
      <FhDataTableColumn data-test-id="bookmark-column" name="isBookmarked">
        <template #header>Bookmarked</template>
        <template #custom="{ rowData }">
          <ItemListBookmark :row="rowData" />
        </template>
      </FhDataTableColumn>

      <FhDataTableColumn align="end" class="w-10">
        <template #custom="{ rowData }">
          <FhDropdownMenu>
            <FhIconButton variant="plain" size="xs" :icon="option">
              View
            </FhIconButton>
            <template #sections>
              <FhDropdownSection>
                <FhDropdownItem @click="redirectToItemDetails(rowData.itemId)">
                  <FhText variant="body-lg">Preview Item Details</FhText>
                </FhDropdownItem>
              </FhDropdownSection>
            </template>
          </FhDropdownMenu>
        </template>
      </FhDataTableColumn>
    </FhDataTable>
  </div>
</template>
